import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useUser } from '@/hooks/use-user'
import { authEvents } from '@/api/http'

// Routes a logged-out visitor may reach. Everything else is gated.
// Root '/' is NOT public: a logged-out visitor is gated to login, and a
// logged-in visitor hits the RootRedirect index route → their dashboard.
const PUBLIC_PATHS = new Set<string>([
  '/login',
  '/privacy',
  '/signup',
  '/signup/register',
  '/signup/success',
  '/user/password/reset',
])

function isPublic(pathname: string): boolean {
  if (PUBLIC_PATHS.has(pathname)) return true
  // /login/* sub-paths are the modal entry too.
  if (pathname === '/login' || pathname.startsWith('/login/')) return true
  return false
}

export function RequireAuth() {
  const { isLoading, isAuthenticated } = useUser()
  const location = useLocation()
  const pub = isPublic(location.pathname)
  const blocked = !isLoading && !isAuthenticated && !pub

  // When a logged-out user hits a gated URL, open the login modal in place
  // (the existing unauthorized→LoginModal flow), preserving where they were
  // headed so login lands them back here.
  useEffect(() => {
    if (blocked) {
      authEvents.dispatchEvent(
        new CustomEvent('unauthorized', {
          detail: { returnUrl: location.pathname + location.search },
        }),
      )
    }
  }, [blocked, location.pathname, location.search])

  if (isLoading) {
    return (
      <div role="status" className="text-center py-12 text-gray-400">
        Loading…
      </div>
    )
  }

  if (blocked) {
    return (
      <div className="text-center py-16 text-gray-600">
        <p className="text-lg font-medium">Log in to view this page</p>
        <p className="mt-1 text-sm text-gray-400">
          Sign in to continue — you’ll come right back here.
        </p>
      </div>
    )
  }

  return <Outlet />
}
